import { z } from 'zod'
import { isCanonicalAccountCode, normalizeAccountCode } from '../domain/accountCode.js'

/**
 * Primitives and enums shared across collections (REQUIREMENTS.md §5).
 *
 * Timestamps are ISO 8601 UTC strings in this layer rather than Firestore
 * `Timestamp` objects, so the schemas stay usable in Cloud Functions, in tests,
 * and in the browser without dragging in a Firebase dependency. Conversion
 * happens at the Firestore boundary in the web app's converters.
 */

/** ISO 8601 UTC timestamp. */
export const isoTimestamp = z.iso.datetime({ offset: false })
export type IsoTimestamp = z.infer<typeof isoTimestamp>

/** Calendar date with no time component, `YYYY-MM-DD`. */
export const isoDate = z.iso.date()
export type IsoDate = z.infer<typeof isoDate>

/** Firestore document ID. */
export const docId = z.string().min(1).max(1500)

/**
 * Signed dollars. Negative values are refunds, reversals, and credits (§5.3).
 *
 * Stored as a number per the spec, but never summed directly — see
 * `domain/budgetMath` for the cents-integer helpers that keep float error out
 * of displayed totals.
 */
export const money = z
  .number()
  .finite()
  .refine((n) => Number.isInteger(Math.round(n * 100)) && Math.abs(n) < 1e9, {
    message: 'Amount must be a finite dollar value.',
  })

/** Non-negative dollars, for beginning balances. */
export const positiveMoney = money.refine((n) => n >= 0, {
  message: 'Amount cannot be negative.',
})

/**
 * A district accounting code, normalized to canonical hyphen-delimited form on
 * parse. Accepts the deprecated space-delimited legacy form as input.
 */
export const accountCode = z
  .string()
  .transform((value, ctx) => {
    const normalized = normalizeAccountCode(value)
    if (normalized === null) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Expected 9 hyphen-delimited numeric segments, e.g. 10-2818-580-000-00-000-000-329-0000',
      })
      return z.NEVER
    }
    return normalized
  })
  .refine(isCanonicalAccountCode)

// ── Enums ────────────────────────────────────────────────────────────────────

/** Which fund a budget code belongs to; drives reporting group (§5.2). */
export const fundEnum = z.enum(['main', 'grant'])
export type Fund = z.infer<typeof fundEnum>

/** Default categorization applied to new transactions on a code (§5.2). */
export const categoryEnum = z.enum([
  'travel',
  'conference',
  'tech',
  'meals',
  'services',
  'other',
])
export type Category = z.infer<typeof categoryEnum>

/**
 * How money reached the ledger (§5.3). Determines which confirmation path a
 * transaction follows — see `reconciledViaEnum`.
 */
export const sourceEnum = z.enum([
  'pcard',
  'amazon',
  'po',
  'reimbursement',
  'manual',
  'csiu',
])
export type Source = z.infer<typeof sourceEnum>

/**
 * Sources that appear on the paper credit-card statement and are therefore
 * confirmed by packet submission rather than by CSIU import.
 */
export const PACKET_CONFIRMED_SOURCES: readonly Source[] = ['pcard']

/**
 * Sources that flow through the accounting system and are confirmed when a
 * CSIU import matches them. P-Card is deliberately absent: those rows exist in
 * the CSIU export but §6.4 skips them, so they can never be matched.
 */
export const CSIU_CONFIRMED_SOURCES: readonly Source[] = [
  'amazon',
  'po',
  'reimbursement',
  'csiu',
]

export const transactionStatusEnum = z.enum([
  'pending',
  'pending_refund_documentation',
  'reconciled',
  'disputed',
  'void',
])
export type TransactionStatus = z.infer<typeof transactionStatusEnum>

/**
 * Statuses that count toward Spent (§7.4). Everything the user is on the hook
 * for, whether or not it has cleared. Only `void` is excluded — a voided
 * transaction represents money that was never actually owed.
 */
export const SPENT_STATUSES: readonly TransactionStatus[] = [
  'pending',
  'pending_refund_documentation',
  'reconciled',
  'disputed',
]

/** Which confirmation path cleared a transaction (§5.3). */
export const reconciledViaEnum = z.enum(['packet', 'csiu'])
export type ReconciledVia = z.infer<typeof reconciledViaEnum>

/**
 * Why a transaction legitimately has no receipt (§5.3). Presence of a reason is
 * what distinguishes "receipt not yet linked" from "no receipt exists".
 */
export const noReceiptReasonEnum = z.enum([
  'parking_meter',
  'transit_fare',
  'other_cash',
  'vendor_did_not_send_refund_confirmation',
])
export type NoReceiptReason = z.infer<typeof noReceiptReasonEnum>

export const captureSourceEnum = z.enum([
  'email_forward',
  'mobile_capture',
  'desktop_upload',
])
export type CaptureSource = z.infer<typeof captureSourceEnum>

export const reconciliationStatusEnum = z.enum([
  'open',
  'statement_ingested',
  'matched',
  'packet_generated',
  'submitted',
])
export type ReconciliationStatus = z.infer<typeof reconciliationStatusEnum>
